import React, { useContext, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { ArrowDownOutlined, NumberOutlined, PauseCircleOutlined, WarningFilled } from "@ant-design/icons";
import { AuthContext } from "../../../../services/auth.service";
import { canAccessPage } from "../../../../utils/subscriptionUtils";
import { useTagMonitorData } from "../../hooks/useTagMonitorData";
import { useDataLayerGuardData } from "../../hooks/useDataLayerGuardData";
import { getTodayPeriod } from "../../utils";
import { QUICK_ACTIONS_INFO } from "../../tooltips";
import { withoutExcludedTags } from "../TagMonitorSection";
import HomeCard from "../HomeCard";
import ChartNoData from "../ChartNoData";
import QuickActionItem from "../QuickActionItem";
import {
  AVERAGE_DAYS,
  getTags,
  getUnnamedTagIds,
  describeUnnamedTags,
  getTopVolumeDrop,
  describeInactiveTags,
  getActionableInactiveTags,
  getLastRunErrorCount,
  describeLastRunErrors,
} from "./utils";
import "./styles.scss";

const BULK_EDIT_LINK = "/authorize_user";
const TAG_MONITOR_DASHBOARD_LINK = "/tag_monitor_dashboard";
const DATALAYER_GUARD_LINK = "/datalayer_guard_dashboard";

// Fixed period, so the to-do list does not shrink with the date filter: today, yesterday and the 7 days before
const PERIOD_DAYS = AVERAGE_DAYS + 2;

/**
 * QuickActions Component - Card with the open to-dos of the company on the homepage
 *
 * @param {function} onLoadingChange - Called with true or false when the loading state changes, for the loading screen
 */
const QuickActions = ({ onLoadingChange }) => {
  const { companyInfo } = useContext(AuthContext);

  const hasTagMonitor = canAccessPage({ pageKey: "tag_monitor_dashboard", companyInfo });
  const hasDataLayerGuard = canAccessPage({ pageKey: "datalayer_guard_dashboard", companyInfo });

  const period = useMemo(
    () => ({ start: dayjs().subtract(PERIOD_DAYS - 1, "day"), end: dayjs(), window: null }),
    [],
  );
  // Per hour ("one_day" window), so the last DataLayer Guard run can be found in the snapshot timeline
  const dataLayerGuardPeriod = useMemo(() => ({ ...period, window: "one_day" }), [period]);
  // The full rows, the quick actions need the calls per tag per day
  const errorsContext = useTagMonitorData("errors", period, hasTagMonitor, "Home Quick actions", null);
  const dataLayerGuardContext = useDataLayerGuardData(dataLayerGuardPeriod, hasDataLayerGuard, "Home Quick actions");
  // The same request as the inactive tags card
  const todayPeriod = useMemo(() => getTodayPeriod(), []);
  const inactivityContext = useTagMonitorData("tag-inactivity", todayPeriod, hasTagMonitor, "Home Quick actions");

  const loading =
    (hasTagMonitor && (errorsContext === undefined || inactivityContext === undefined)) ||
    (hasDataLayerGuard && dataLayerGuardContext === undefined);
  const loadFailed = [errorsContext, inactivityContext, dataLayerGuardContext].includes(null);

  // The homepage shows its loading screen until every part of the page is loaded
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const actions = useMemo(() => {
    // Wait for all data, so the actions don't jump around while loading
    if (loading) return [];

    const tags = getTags(errorsContext);
    const unnamedTagIds = getUnnamedTagIds(tags);
    const allInactiveTags = withoutExcludedTags(inactivityContext?.inactive_tags, inactivityContext);
    const inactiveTags = getActionableInactiveTags(allInactiveTags);
    // A tag that is inactive is not also shown as a volume drop
    const volumeDrop = getTopVolumeDrop(
      tags,
      new Set(allInactiveTags.map((tag) => `${tag.data_type}-${tag.container_id}-${tag.tag_id}`)),
    );
    const lastRunErrors = getLastRunErrorCount(dataLayerGuardContext);

    // Only the actions with something to do are shown
    return [
      inactiveTags.length > 0 && {
        key: "inactive-tags",
        icon: <PauseCircleOutlined />,
        color: "pink",
        title: `${inactiveTags.length} ${inactiveTags.length === 1 ? "tag" : "tags"} inactive`,
        description: describeInactiveTags(inactiveTags),
        // Tag volume of the side of the first (most important) inactive tag
        link: `${TAG_MONITOR_DASHBOARD_LINK}?view=${inactiveTags[0].data_type}&tab=tag_volume`,
        linkText: "View more",
      },
      unnamedTagIds.length > 0 && {
        key: "tag-names",
        icon: <NumberOutlined />,
        color: "orange",
        title: "Configure missing tag names",
        description: describeUnnamedTags(unnamedTagIds),
        link: BULK_EDIT_LINK,
        linkText: "Bulk edit",
      },
      volumeDrop && {
        key: "volume-drop",
        icon: <ArrowDownOutlined />,
        color: "purple",
        title: `${volumeDrop.tag.tagName ?? volumeDrop.tag.tagId} dropped ${volumeDrop.dropPercentage}%`,
        // Tag names and IDs are only unique within a container
        description: `Well below the ${AVERAGE_DAYS}-day average${
          volumeDrop.tag.containerId ? ` in ${volumeDrop.tag.containerId}` : ""
        }`,
        // The dashboard opens on the tag volume table of the side of the tag
        link: `${TAG_MONITOR_DASHBOARD_LINK}?view=${volumeDrop.tag.side}&tab=tag_volume`,
        linkText: "View more",
      },
      lastRunErrors > 0 && {
        key: "datalayer-error",
        icon: <WarningFilled />,
        color: "pink",
        title: "Resolve datalayer errors from last run",
        description: describeLastRunErrors(lastRunErrors),
        link: DATALAYER_GUARD_LINK,
        linkText: "View errors",
      },
    ].filter(Boolean);
  }, [loading, errorsContext, inactivityContext, dataLayerGuardContext]);

  // A failed request must not look like "nothing to do"
  const emptyMessage = loadFailed ? "Quick actions could not be loaded." : "No open actions.";

  return (
    <HomeCard title="Quick actions"  className="quick-actions">
      {actions.map(({ key, ...action }) => (
        <QuickActionItem key={key} {...action} />
      ))}
      {actions.length === 0 && <ChartNoData show={!loading} message={emptyMessage} />}
      {actions.length > 0 && loadFailed && (
        <p className="quick-actions-load-error">Not all quick actions could be loaded.</p>
      )}
    </HomeCard>
  );
};

export default QuickActions;
